import { getDatabase } from '../index';
import { calculateDiff, generateLocalId, isLocalId } from '../sync/diffUtils';
import * as PendingRepository from './PendingRepository';

/**
 * CaseRepository
 * 
 * CRUD operations for cases with automatic diff tracking.
 * Changes are stored locally and queued for sync to server.
 */

/**
 * Default case values
 */
const DEFAULT_CASE = {
  name: '',
  number: '',
  details: '',
  juryBoxSize: 12,
  jurorsPerRow: 6,
  numberOfAlternates: 0,
  spacers: [],
  demographicCategories: ['Race', 'Age', 'Gender'],
  prosecutionPeremptStrikes: 6,
  defendantPeremptStrikes: 6,
  alternateProsecutionPeremptStrikes: 2,
  alternateDefendantPeremptStrikes: 2,
  civilCase: false,
  revision: 0
};

/**
 * Get all cases
 * @returns {Promise<Array>}
 */
export async function getAll() {
  const db = getDatabase();
  if (!db) return [];

  const cases = await db.cases.toArray();
  return cases.sort((a, b) => (b.updatedAt || '').localeCompare(a.updatedAt || ''));
}

/**
 * Get a case by ID
 * @param {string} caseId - Case ID
 * @returns {Promise<Object|null>}
 */
export async function getById(caseId) {
  const db = getDatabase();
  if (!db) return null;

  return db.cases.get(caseId);
}

/**
 * Check if a case exists
 * @param {string} caseId - Case ID
 * @returns {Promise<boolean>}
 */
export async function exists(caseId) {
  const db = getDatabase();
  if (!db) return false;

  const count = await db.cases.where('_id').equals(caseId).count();
  return count > 0;
}

/**
 * Create a new case (locally)
 * @param {Object} caseData - Case data
 * @param {string} creatorEmail - Creator's email
 * @returns {Promise<Object>} - Created case with local ID
 */
export async function create(caseData, creatorEmail) {
  const db = getDatabase();
  if (!db) throw new Error('Database not initialized');

  const localId = generateLocalId();
  const now = new Date().toISOString();

  const newCase = {
    ...DEFAULT_CASE,
    ...caseData,
    _id: localId,
    creator: creatorEmail || caseData.creator || '',
    canShare: true,
    createdAt: now,
    updatedAt: now,
    revision: 0,
    _localOnly: true,
    _localId: localId
  };

  // Save to local database
  await db.cases.add(newCase);

  // Queue for sync
  await PendingRepository.addCreate(
    PendingRepository.EntityType.CASE,
    localId,
    newCase
  );

  console.log(`Case created locally: ${localId}`);
  return newCase;
}

/**
 * Update a case (locally)
 * Only changed fields are tracked for sync
 * @param {string} caseId - Case ID
 * @param {Object} updates - Fields to update
 * @returns {Promise<Object|null>} - Updated case
 */
export async function update(caseId, updates) {
  const db = getDatabase();
  if (!db) throw new Error('Database not initialized');

  // Get current case
  const existing = await db.cases.get(caseId);
  if (!existing) {
    console.warn(`Case not found for update: ${caseId}`);
    return null;
  }

  const now = new Date().toISOString();

  // Calculate what actually changed (diff against full merged state to avoid
  // treating absent keys as deletions when updates is a partial object)
  const diff = calculateDiff(existing, { ...existing, ...updates });
  
  if (!diff) {
    console.log(`No changes detected for case: ${caseId}`);
    return existing;
  }

  // Apply updates locally
  const updatedCase = {
    ...existing,
    ...updates,
    updatedAt: now
  };

  await db.cases.put(updatedCase);

  // Queue the diff for sync (only if not a local-only case that's already pending)
  if (!existing._localOnly) {
    await PendingRepository.addUpdate(
      PendingRepository.EntityType.CASE,
      caseId,
      { ...diff, revision: existing.revision },
      existing.revision
    );
  }

  console.log(`Case updated locally: ${caseId}`, Object.keys(diff));
  return updatedCase;
}

/**
 * Delete a case (locally)
 * @param {string} caseId - Case ID
 * @returns {Promise<boolean>} - True if deleted
 */
export async function remove(caseId) {
  const db = getDatabase();
  if (!db) throw new Error('Database not initialized');

  const existing = await db.cases.get(caseId);
  if (!existing) {
    return false;
  }

  // Delete locally
  await db.cases.delete(caseId);

  // Also delete all jurors for this case
  await db.jurors.where('caseId').equals(caseId).delete();

  // Queue for sync (if not local-only)
  await PendingRepository.addDelete(
    PendingRepository.EntityType.CASE,
    caseId
  );

  console.log(`Case deleted locally: ${caseId}`);
  return true;
}

/**
 * Upsert a case (create or update without generating pending operations)
 * Used by SyncEngine when applying server data locally and during migration.
 * @param {Object} caseData - Case data (must include _id)
 */
export async function upsert(caseData) {
  const db = getDatabase();
  if (!db) throw new Error('Database not initialized');

  await db.cases.put({
    ...DEFAULT_CASE,
    _localOnly: false,
    ...caseData
  });
}

/**
 * Save case from server (no pending operation created)
 * Used when receiving data from server sync
 * @param {Object} caseData - Case data from server
 */
export async function saveFromServer(caseData) {
  const db = getDatabase();
  if (!db) throw new Error('Database not initialized');

  // Ensure required fields
  const caseToSave = {
    ...DEFAULT_CASE,
    ...caseData,
    _localOnly: false,
    _localId: null
  };

  await db.cases.put(caseToSave);
  console.log(`Case saved from server: ${caseData._id}`);
}

/**
 * Save multiple cases from server (bulk operation)
 * @param {Array} cases - Array of cases from server
 */
export async function bulkSaveFromServer(cases) {
  const db = getDatabase();
  // The database can close mid-fetch (e.g. session expiry logs the user out
  // while a server round-trip is in flight) — that's an expected race, not
  // an error worth surfacing.
  if (!db) {
    console.warn('bulkSaveFromServer skipped: database not initialized');
    return;
  }

  const casesToSave = cases.map(c => ({
    ...DEFAULT_CASE,
    ...c,
    _localOnly: false,
    _localId: null
  }));

  await db.cases.bulkPut(casesToSave);
  console.log(`${cases.length} cases saved from server`);
}

/**
 * Update local case ID after server assigns real ID
 * @param {string} localId - Local UUID
 * @param {string} serverId - Server-assigned ID
 * @param {Object} serverData - Full case data from server
 */
export async function updateIdFromServer(localId, serverId, serverData) {
  const db = getDatabase();
  // Same expected race as bulkSaveFromServer above.
  if (!db) {
    console.warn('updateIdFromServer skipped: database not initialized');
    return;
  }

  // Delete the local record
  await db.cases.delete(localId);

  // Add the server record
  await saveFromServer(serverData);

  // Update jurors to reference new case ID
  const jurors = await db.jurors.where('caseId').equals(localId).toArray();
  for (const juror of jurors) {
    await db.jurors.update(juror._id, { caseId: serverId });
  }

  // Update pending operations
  await PendingRepository.updateEntityId(
    PendingRepository.EntityType.CASE,
    localId,
    serverId
  );

  console.log(`Case ID updated: ${localId} -> ${serverId}`);
}

/**
 * Apply a diff from server (WebSocket update)
 * @param {string} caseId - Case ID
 * @param {Object} diff - Changed fields from server
 */
export async function applyServerDiff(caseId, diff) {
  const db = getDatabase();
  if (!db) return;

  const existing = await db.cases.get(caseId);
  if (!existing) {
    console.warn(`Case not found for server diff: ${caseId}`);
    return;
  }

  // Check revision to avoid overwriting newer local changes
  if (diff.revision && existing.revision > diff.revision) {
    console.log(`Ignoring older server diff for case: ${caseId}`);
    return;
  }

  const updated = {
    ...existing,
    ...diff,
    updatedAt: new Date().toISOString()
  };

  await db.cases.put(updated);
  console.log(`Server diff applied to case: ${caseId}`);
}

/**
 * Get cases by creator email
 * @param {string} email - Creator email
 * @returns {Promise<Array>}
 */
export async function getByCreator(email) {
  const db = getDatabase();
  if (!db) return [];

  return db.cases.where('creator').equals(email).toArray();
}

/**
 * Check if case has unsynchronized local changes
 * @param {string} caseId - Case ID
 * @returns {Promise<boolean>}
 */
export async function hasLocalChanges(caseId) {
  const caseData = await getById(caseId);
  if (!caseData) return false;

  if (caseData._localOnly) return true;

  const pendingOps = await PendingRepository.getForCase(caseId);
  return pendingOps.some(op => 
    op.entityType === PendingRepository.EntityType.CASE
  );
}

/**
 * Clear all cases (for testing/reset)
 */
export async function clearAll() {
  const db = getDatabase();
  if (!db) return;

  await db.cases.clear();
  console.log('All cases cleared');
}

export default {
  getAll,
  getById,
  exists,
  create,
  update,
  remove,
  upsert,
  saveFromServer,
  bulkSaveFromServer,
  updateIdFromServer,
  applyServerDiff,
  getByCreator,
  hasLocalChanges,
  clearAll
};
